import { css, cx } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import type { MouseEventHandler } from "react";
import type React from "react";
import type { DesignSystemLinkHref, ShowLinkAsActive } from "../../routing/OctopusRoutingContext";
import type { SideNavBarButtonTabAriaAttributes } from "./SideNavBarIconButtonTab";
import { SideNavBarIconButtonTab } from "./SideNavBarIconButtonTab";
import { SideNavBarLinkTab } from "./SideNavBarLinkTab";
interface SharedSideNavBarTabLink {
    /**
     * The type of vertical menu item.
     */
    type: "link";
    /**
     * The icon for this link.
     */
    icon: React.ReactNode;
    /**
     * The link location.
     */
    href: DesignSystemLinkHref;
    /**
     * Controls when this link is shown as active.
     */
    showLinkAsActive?: ShowLinkAsActive;
    /**
     * Controls the visibility of this link.
     */
    isVisible?: boolean;
}
interface SideNavBarTabLinkIconOnly extends SharedSideNavBarTabLink {
    /**
     * The required accessible name for this icon link.
     */
    accessibleName: string;
}
interface SideNavBarTabLinkIconWithLabel extends SharedSideNavBarTabLink {
    /**
     * An optional accessible name for this icon link. If no accessible name is provided the label will be used.
     */
    accessibleName?: string;
    /**
     * The label for this icon link.
     */
    label: string;
}
export type SideNavBarTabLink = SideNavBarTabLinkIconOnly | SideNavBarTabLinkIconWithLabel;
type SideNavBarCustomItem = {
    /**
     * The type of vertical menu item.
     */
    type: "custom";
    /**
     * A locally unique identifier for the custom item.
     */
    key: string;
    /**
     * A custom react element to use for the item.
     */
    content: React.ReactElement;
};
type SideNavBarIconButtonTab = {
    /**
     * The type of vertical menu item.
     */
    type: "icon-button";
    /**
     * An accessible name for the icon button.
     */
    accessibleName: string;
    /**
     * The icon to use for the icon button.
     */
    icon: React.ReactNode;
    /**
     * The onClick handler associated with the icon button.
     */
    onClick?: MouseEventHandler<Element>;
    /**
     * Additional ARIA menu attributes which can be attached to this icon button if it's
     * being used to show a menu.
     */
    menuAttributes?: SideNavBarButtonTabAriaAttributes;
};
export type SideNavBarItem = SideNavBarTabLink | SideNavBarIconButtonTab | SideNavBarCustomItem;
export type BottomSideNavBarItem = SideNavBarItem;
export type TopSideNavBarItem = SideNavBarTabLinkIconWithLabel;
export interface SideNavBarProps {
    topItems: ReadonlyArray<TopSideNavBarItem>;
    bottomItems: ReadonlyArray<BottomSideNavBarItem>;
    onLinkClicked?: SideNavBarTabLinkClickHandler;
    onHoveredLinkChanged?: SideNavBarTabHoveredLinkChangedHandler;
}
export function SideNavBar({ topItems, bottomItems, onLinkClicked, onHoveredLinkChanged }: SideNavBarProps) {
    return (
    // adora-unmask is temporary for Adora trial - #team-growth-experience-requests-and-discussion
    <div className={cx(sideNavBarStyles.container, "adora-unmask")}>
            <div className={sideNavBarStyles.topItems}>
                {topItems.filter(isItemVisible).map((x) => (<SideNavBarItemComponent key={x.label} item={x} onLinkClicked={onLinkClicked} onHoveredLinkChanged={onHoveredLinkChanged}/>))}
            </div>
            <div className={sideNavBarStyles.bottomItems}>
                {bottomItems.filter(isItemVisible).map((x) => (<SideNavBarItemComponent key={getSideNavBarItemReactKey(x)} item={x} onLinkClicked={onLinkClicked} onHoveredLinkChanged={onHoveredLinkChanged}/>))}
            </div>
        </div>);
}
const sideNavBarStyles = {
    container: css({
        display: "flex",
        height: "100%",
        flexDirection: "column",
        alignItems: "stretch",
        width: "4.25rem",
        paddingTop: space[16],
        paddingBottom: space[16],
    }),
    topItems: css({
        display: "flex",
        flex: "1",
        flexDirection: "column",
        justifyItems: "stretch",
        rowGap: space[6],
    }),
    bottomItems: css({
        display: "flex",
        justifyItems: "stretch",
        flexDirection: "column",
        rowGap: space[4],
    }),
};
function getSideNavBarItemReactKey(item: SideNavBarItem): string {
    if (item.type === "custom") {
        return item.key;
    }
    if (isSideBarIconButton(item)) {
        return item.accessibleName;
    }
    return isSideBarItemTabWithLabel(item) ? item.label : item.accessibleName;
}
function isSideBarIconButton(item: SideNavBarItem): item is SideNavBarIconButtonTab {
    return item.type === "icon-button";
}
function isSideBarItemTabWithLabel(item: SideNavBarTabLink): item is SideNavBarTabLinkIconWithLabel {
    return "label" in item && Boolean(item.label);
}
type SideNavBarTabLinkClickHandler = (event: React.MouseEvent, item: SideNavBarTabLink) => void;
type SideNavBarTabHoveredLinkChangedHandler = (item: SideNavBarTabLink | undefined) => void;
interface SideNavBarItemComponentProps {
    item: SideNavBarItem;
    onLinkClicked?: SideNavBarTabLinkClickHandler;
    onHoveredLinkChanged?: SideNavBarTabHoveredLinkChangedHandler;
}
function SideNavBarItemComponent({ item, onLinkClicked, onHoveredLinkChanged }: SideNavBarItemComponentProps): React.ReactElement {
    const { type } = item;
    switch (type) {
        case "link":
            const onClick: MouseEventHandler<Element> = (event) => {
                onLinkClicked?.(event, item);
            };
            const handleIsHoveredChanged = (isHovered: boolean) => {
                onHoveredLinkChanged?.(isHovered ? item : undefined);
            };
            return isSideBarItemTabWithLabel(item) ? (<SideNavBarLinkTab icon={item.icon} href={item.href} accessibleName={item.accessibleName} showLinkAsActive={item.showLinkAsActive} label={item.label} onClick={onClick} onIsHoveredChanged={handleIsHoveredChanged}/>) : (<SideNavBarLinkTab icon={item.icon} href={item.href} accessibleName={item.accessibleName} showLinkAsActive={item.showLinkAsActive} onClick={onClick} onIsHoveredChanged={handleIsHoveredChanged}/>);
        case "custom":
            return <CustomContent>{item.content}</CustomContent>;
        case "icon-button":
            return <SideNavBarIconButtonTab accessibleName={item.accessibleName} icon={item.icon} onClick={item.onClick} {...item.menuAttributes}/>;
        default:
            exhaustiveCheck(type, `Unsupported SideNavBar item type "${type}"`);
    }
}
function isItemVisible(item: SideNavBarItem) {
    return item.type !== "link" || item.isVisible !== false;
}
function CustomContent({ children }: React.PropsWithChildren<{}>) {
    return <div className={customContentStyles}>{children}</div>;
}
const customContentStyles = css({
    display: "flex",
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
});
